import { useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { ApiError, confirmEmailVerification } from "@/lib/api";
import { Button } from "@/components/ui/Button";

/**
 * M9a C1 — `/verify-email?token=` landing.
 *
 * The token in the query string auto-populates the input. Submit
 * POSTs `/api/me/email-verification/confirm` (anonymous endpoint —
 * token IS the auth). Success offers a deep-link to `/me?tab=email`.
 *
 * Privacy invariant (C3): the token is read once from the search
 * param and copied into a normal `<input>`; never logged, never
 * preserved in the DOM after submit (cleared in `onSuccess`).
 */
export function VerifyEmailPage() {
  const search = useSearch({ from: "/verify-email" });
  const [token, setToken] = useState(search.token ?? "");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (t: string) => confirmEmailVerification({ token: t.trim() }),
    onSuccess: () => {
      setSuccess(true);
      setToken("");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "That verification token is invalid or expired.",
        );
      } else {
        setError(formatErr(err));
      }
    },
  });

  return (
    <main
      data-testid="verify-email-page"
      className="min-h-screen bg-canvas flex items-center justify-center p-4"
    >
      <section
        aria-label="Verify your email"
        className="w-full max-w-md rounded-lg border border-rule bg-surface p-6 flex flex-col gap-3"
      >
        <header>
          <h1 className="text-xl font-display tracking-tight text-ink">
            Verify email
          </h1>
          <p className="text-ink-muted text-xs">
            Paste the verification token from your email to confirm ownership.
          </p>
        </header>

        {success ? (
          <div className="flex flex-col gap-2">
            <p
              data-testid="verify-email-success"
              className="text-good text-sm font-mono"
            >
              Email verified. Thank you for confirming.
            </p>
            <Link
              to="/me"
              search={{ tab: "email" }}
              data-testid="verify-email-open-me"
              className="text-xs text-accent hover:underline self-start"
            >
              Open your account → Email tab
            </Link>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              if (token.trim().length < 16) {
                setError("Verification token must be at least 16 characters.");
                return;
              }
              mut.mutate(token);
            }}
            className="flex flex-col gap-2"
          >
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-display uppercase tracking-wider text-ink-muted">
                Verification token
              </span>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                autoFocus
                data-testid="verify-email-token"
                className="rounded border border-rule bg-bg-card px-2 py-1.5 text-xs font-mono text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>

            {error ? (
              <p
                data-testid="verify-email-error"
                className="text-destructive text-xs font-mono"
              >
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              size="sm"
              disabled={mut.isPending}
              data-testid="verify-email-submit"
              className="self-start"
            >
              {mut.isPending ? "Verifying…" : "Verify email"}
            </Button>
          </form>
        )}
      </section>
    </main>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string"
      ? err.detail
      : `HTTP ${err.status}`;
  }
  return "Network error. Try again.";
}
